import { useQuery } from '@tanstack/react-query';
import { useId, useState, type FormEvent } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';

import { useAuth } from '../auth/auth-context';
import { SentimentStrip, StripLegend } from '../components/sentiment';
import { SiteFooter } from '../components/site-footer';
import { SiteHeader } from '../components/site-header';
import {
  ApiError,
  addToWatchlist,
  deleteWatchlist,
  fetchCatalog,
  removeFromWatchlist,
  renameWatchlist,
  type Watchlist,
} from '../lib/api';
import { usePageTitle } from '../lib/page-title';
import { formatScore, labelFor, toneFor } from '../lib/sentiment';
import {
  summarizeWatchlist,
  useWatchlistAction,
  useWatchlistCache,
  useWatchlists,
  type RankOrder,
  type WatchlistTicker,
} from '../lib/watchlists';

const TONE_CAPTION = {
  positive: 'Leaning positive',
  neutral: 'Mostly neutral',
  negative: 'Leaning negative',
} as const;

function formatDay(day: string): string {
  // Days are calendar dates; format in UTC so the label never shifts a day.
  return new Date(`${day}T00:00:00Z`).toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    timeZone: 'UTC',
  });
}

function plural(count: number, noun: string): string {
  return `${count} ${noun}${count === 1 ? '' : 's'}`;
}

type Cache = ReturnType<typeof useWatchlistCache>;

function RemoveButton({
  watchlist,
  ticker,
  token,
  cache,
  action,
}: {
  watchlist: Watchlist;
  ticker: WatchlistTicker;
  token: string;
  cache: Cache;
  action: ReturnType<typeof useWatchlistAction>;
}) {
  return (
    <button
      type="button"
      className="button-quiet button-inline watchlist-remove"
      aria-label={`Remove ${ticker.symbol} from ${watchlist.name}`}
      disabled={action.busy}
      onClick={() =>
        void action.run(
          async () => cache.replaced(await removeFromWatchlist(token, watchlist.id, ticker.symbol)),
          ticker.symbol,
        )
      }
    >
      Remove
    </button>
  );
}

/** Rename, add tickers, and delete: tucked away until the user asks for them. */
function EditPanel({
  id,
  watchlist,
  token,
  cache,
  maxTickers,
}: {
  id: string;
  watchlist: Watchlist;
  token: string;
  cache: Cache;
  maxTickers: number;
}) {
  const baseId = useId();
  const navigate = useNavigate();
  const [nameDraft, setNameDraft] = useState(watchlist.name);
  const [symbolDraft, setSymbolDraft] = useState('');
  const [confirmDelete, setConfirmDelete] = useState(false);
  const rename = useWatchlistAction();
  const add = useWatchlistAction();
  const remove = useWatchlistAction();
  // Only feeds the add-ticker suggestions; editing works without it.
  const catalog = useQuery({
    queryKey: ['catalog', token],
    queryFn: () => fetchCatalog(token),
    enabled: Boolean(token),
  });

  const present = new Set(watchlist.tickers.map((ticker) => ticker.symbol));
  const suggestions = (catalog.data?.tickers ?? []).filter((t) => !present.has(t.symbol));
  const full = watchlist.tickers.length >= maxTickers;

  async function submitRename(event: FormEvent) {
    event.preventDefault();
    await rename.run(async () =>
      cache.replaced(await renameWatchlist(token, watchlist.id, nameDraft.trim())),
    );
  }

  async function submitAdd(event: FormEvent) {
    event.preventDefault();
    const symbol = symbolDraft.trim().toUpperCase();
    const added = await add.run(
      async () => cache.replaced(await addToWatchlist(token, watchlist.id, symbol)),
      symbol,
    );
    if (added) setSymbolDraft('');
  }

  async function confirmDeletion() {
    const deleted = await remove.run(() => deleteWatchlist(token, watchlist.id));
    if (deleted) {
      // Leave the page first so it never renders a "not found" flash.
      await navigate('/watchlists', { replace: true });
      cache.removed(watchlist.id);
    }
  }

  return (
    <section id={id} className="watchlist-edit" aria-label="Edit list">
      <form className="watchlist-edit-field" onSubmit={(event) => void submitAdd(event)}>
        <label htmlFor={`${baseId}-add`}>Add a ticker</label>
        <div className="inline-form">
          <input
            id={`${baseId}-add`}
            type="text"
            list={`${baseId}-catalog`}
            placeholder={full ? `Full (${maxTickers} tickers)` : 'Symbol, e.g. AAPL'}
            autoComplete="off"
            value={symbolDraft}
            onChange={(event) => setSymbolDraft(event.target.value)}
            disabled={full}
          />
          <datalist id={`${baseId}-catalog`}>
            {suggestions.map((ticker) => (
              <option key={ticker.symbol} value={ticker.symbol}>
                {ticker.name}
              </option>
            ))}
          </datalist>
          <button
            type="submit"
            className="button-inline"
            disabled={add.busy || full || !symbolDraft.trim()}
          >
            Add
          </button>
        </div>
        {add.error ? (
          <p className="form-error" role="alert">
            {add.error}
          </p>
        ) : null}
      </form>

      <form className="watchlist-edit-field" onSubmit={(event) => void submitRename(event)}>
        <label htmlFor={`${baseId}-name`}>List name</label>
        <div className="inline-form">
          <input
            id={`${baseId}-name`}
            type="text"
            maxLength={60}
            value={nameDraft}
            onChange={(event) => setNameDraft(event.target.value)}
          />
          <button
            type="submit"
            className="button-quiet button-inline"
            disabled={rename.busy || !nameDraft.trim() || nameDraft.trim() === watchlist.name}
          >
            Rename
          </button>
        </div>
        {rename.error ? (
          <p className="form-error" role="alert">
            {rename.error}
          </p>
        ) : null}
      </form>

      <div className="watchlist-edit-danger">
        {confirmDelete ? (
          <div
            className="notice notice-warn watchlist-confirm"
            role="group"
            aria-label="Confirm delete"
          >
            <span>
              Delete “{watchlist.name}”
              {watchlist.tickers.length
                ? ` and its ${plural(watchlist.tickers.length, 'ticker')}`
                : ''}
              ? This can’t be undone.
            </span>
            <div className="button-row">
              <button
                type="button"
                className="button-inline"
                disabled={remove.busy}
                onClick={() => void confirmDeletion()}
              >
                Delete watchlist
              </button>
              <button
                type="button"
                className="button-quiet button-inline"
                onClick={() => setConfirmDelete(false)}
              >
                Keep it
              </button>
            </div>
          </div>
        ) : (
          <button
            type="button"
            className="button-quiet button-inline"
            onClick={() => setConfirmDelete(true)}
          >
            Delete list
          </button>
        )}
        {remove.error ? (
          <p className="form-error" role="alert">
            {remove.error}
          </p>
        ) : null}
      </div>
    </section>
  );
}

function OrderToggle({
  order,
  onChange,
}: {
  order: RankOrder;
  onChange: (order: RankOrder) => void;
}) {
  return (
    <div className="segmented" role="group" aria-label="Rank order">
      {(
        [
          ['highest', 'Highest first'],
          ['lowest', 'Lowest first'],
        ] as const
      ).map(([value, label]) => (
        <button
          key={value}
          type="button"
          aria-pressed={order === value}
          className={order === value ? 'segmented-option active' : 'segmented-option'}
          onClick={() => onChange(value)}
        >
          {label}
        </button>
      ))}
    </div>
  );
}

/** One named list: its average, its tickers ranked by score, and (on request) editing. */
export function WatchlistPage() {
  const { id = '' } = useParams();
  const { session } = useAuth();
  const token = session?.access_token ?? '';
  const watchlists = useWatchlists(token);
  const cache = useWatchlistCache(token);
  const editId = useId();
  const [searchParams, setSearchParams] = useSearchParams();
  const order: RankOrder = searchParams.get('order') === 'lowest' ? 'lowest' : 'highest';
  const [editOpen, setEditOpen] = useState(false);
  // Row removals share one action: one request at a time, one error line.
  const removeRow = useWatchlistAction();

  const watchlist = watchlists.data?.watchlists.find((w) => String(w.id) === id);
  // An empty list has nothing to show but the way to fill it, so editing
  // opens and stays open as its first tickers arrive, until the user is done.
  if (watchlist?.tickers.length === 0 && !editOpen) setEditOpen(true);
  usePageTitle(watchlist ? watchlist.name : 'Watchlist');

  let body;
  if (watchlists.isPending) {
    body = (
      <p className="section-state" role="status">
        Loading the watchlist…
      </p>
    );
  } else if (watchlists.isError) {
    body =
      watchlists.error instanceof ApiError && watchlists.error.status === 401 ? (
        <p className="section-state state-error" role="alert">
          <span>Your session has expired.</span>
          <Link className="text-link" to="/signin">
            Sign in again
          </Link>
        </p>
      ) : (
        <p className="section-state state-error" role="alert">
          <span>Could not load the watchlist.</span>
          <button
            type="button"
            className="button-quiet button-inline"
            onClick={() => void watchlists.refetch()}
          >
            Retry
          </button>
        </p>
      );
  } else if (!watchlist) {
    body = (
      <p className="section-state" role="status">
        <span>This watchlist doesn’t exist or was deleted.</span>
        <Link className="text-link" to="/watchlists">
          Back to your watchlists
        </Link>
      </p>
    );
  } else {
    const { average, ranked, unranked } = summarizeWatchlist(watchlist, order);
    const count = watchlist.tickers.length;
    const editing = editOpen;

    body = (
      <>
        <div className="ticker-head watchlist-head">
          <div>
            <nav className="crumbs" aria-label="Breadcrumb">
              <Link to="/watchlists">Watchlists</Link>
              <span aria-hidden="true">/</span>
              <span aria-current="page">{watchlist.name}</span>
            </nav>
            <h1 className="watchlist-title">{watchlist.name}</h1>
            <p className="ticker-name">{plural(count, 'ticker')}</p>
          </div>
          {count > 0 ? (
            <button
              type="button"
              className="button-quiet"
              aria-expanded={editing}
              aria-controls={editId}
              onClick={() => setEditOpen(!editOpen)}
            >
              {editing ? 'Done' : 'Edit list'}
            </button>
          ) : null}
        </div>

        {editing ? (
          <EditPanel
            id={editId}
            watchlist={watchlist}
            token={token}
            cache={cache}
            maxTickers={watchlists.data.max_tickers}
          />
        ) : null}

        {count > 0 ? (
          <section className="ticker-section" aria-label="List score">
            <header className="ticker-section-header">
              <h2>List score</h2>
            </header>
            {average ? (
              <div className="overview">
                <div className="overview-score">
                  <span
                    className={`figure tone-${toneFor(average.score)}`}
                    data-testid="list-score"
                  >
                    {formatScore(average.score)}
                  </span>
                  <span className="caption">
                    {TONE_CAPTION[toneFor(average.score)]}: the average of the latest daily score of{' '}
                    {ranked.length === count
                      ? plural(count, 'ticker')
                      : `${ranked.length} of ${count} tickers`}
                  </span>
                </div>
                <div className="overview-detail">
                  <SentimentStrip {...average} large />
                  <StripLegend {...average} />
                </div>
              </div>
            ) : (
              <p className="section-state" role="status">
                None of these tickers has a score you can see yet.
              </p>
            )}
          </section>
        ) : null}

        <section className="ticker-section" aria-label="Ranking">
          <header className="ticker-section-header watchlist-ranking-header">
            <h2>Ranking</h2>
            {ranked.length > 1 ? (
              <OrderToggle
                order={order}
                onChange={(next) =>
                  setSearchParams(next === 'lowest' ? { order: 'lowest' } : {}, { replace: true })
                }
              />
            ) : null}
          </header>
          {count === 0 ? (
            <p className="section-state">No tickers yet. Add one above to start the ranking.</p>
          ) : null}
          {removeRow.error ? (
            <p className="form-error" role="alert">
              {removeRow.error}
            </p>
          ) : null}
          {ranked.length ? (
            <ol
              className="watchlist-ranking"
              aria-label={order === 'highest' ? 'Highest score first' : 'Lowest score first'}
            >
              {ranked.map((ticker, index) => {
                const { latest } = ticker;
                const label = labelFor(latest.positive, latest.negative, latest.neutral);
                return (
                  <li className="watchlist-rank-row" key={ticker.symbol}>
                    <span className="watchlist-rank" aria-hidden="true">
                      {index + 1}
                    </span>
                    <span className="watchlist-rank-name">
                      <Link className="catalog-symbol" to={`/tickers/${ticker.symbol}`}>
                        {ticker.symbol}
                      </Link>
                      <span className="catalog-name">{ticker.name}</span>
                    </span>
                    <span className="watchlist-rank-score">
                      <span className={`sentiment-tag sentiment-${label}`}>
                        {label} {formatScore(latest.score)}
                      </span>
                      <SentimentStrip {...latest} />
                    </span>
                    <span className="watchlist-rank-meta">
                      <time dateTime={latest.day}>{formatDay(latest.day)}</time>
                      <span>{plural(latest.article_count, 'article')}</span>
                    </span>
                    {editing ? (
                      <RemoveButton
                        watchlist={watchlist}
                        ticker={ticker}
                        token={token}
                        cache={cache}
                        action={removeRow}
                      />
                    ) : null}
                  </li>
                );
              })}
            </ol>
          ) : null}
          {unranked.length ? (
            <>
              <h3 className="watchlist-unranked-title">Not ranked</h3>
              <ul className="watchlist-ranking watchlist-unranked">
                {unranked.map((ticker) => (
                  <li className="watchlist-rank-row" key={ticker.symbol}>
                    <span className="watchlist-rank" aria-hidden="true" />
                    <span className="watchlist-rank-name">
                      <Link className="catalog-symbol" to={`/tickers/${ticker.symbol}`}>
                        {ticker.symbol}
                      </Link>
                      <span className="catalog-name">{ticker.name}</span>
                    </span>
                    <span className="watchlist-rank-score">
                      {ticker.locked ? (
                        <>
                          <span className="plan-badge">Pro</span>
                          <span className="watchlist-row-note">Score available on Pro</span>
                        </>
                      ) : (
                        <span className="watchlist-row-note">No scores yet</span>
                      )}
                    </span>
                    <span className="watchlist-rank-meta" />
                    {editing ? (
                      <RemoveButton
                        watchlist={watchlist}
                        ticker={ticker}
                        token={token}
                        cache={cache}
                        action={removeRow}
                      />
                    ) : null}
                  </li>
                ))}
              </ul>
            </>
          ) : null}
        </section>
      </>
    );
  }

  return (
    <>
      <SiteHeader />
      <main className="page">{body}</main>
      <SiteFooter />
    </>
  );
}
